import GameService from '../services/GameService';

class CareManager {
    constructor() {
        this.gameService = new GameService(process.env.REACT_APP_API_URL || '');
        this.careData = null;
        this.isInitialized = false;
        this.events = new Phaser.Events.EventEmitter();
    }

    async initialize(walletAddress) {
        try {
            this.gameService.setWalletAddress(walletAddress);
            await this.loadCareData();
            this.isInitialized = true;
            // console.log('CareManager initialized successfully');
            return true;
        } catch (error) {
            console.error('Failed to initialize CareManager:', error);
            return false;
        }
    }

    async loadCareData() {
        try {
            this.careData = await this.gameService.fetchApi('/api/care/get', {
                method: 'POST',
                body: JSON.stringify({
                    walletAddress: this.gameService.walletAddress
                })
            });
            
            // console.log('Care data loaded:', this.careData);
            return this.careData;
        } catch (error) {
            console.warn('Error loading care data, using defaults:', error.message);
            
            // If care data doesn't exist, create default care data locally
            this.careData = {
                care_score: 75, // Default care score
                consecutive_days: 1,
                total_logins: 1,
                hunger_neglect_count: 0,
                energy_neglect_count: 0,
                feeding_frequency: 1.0,
                interaction_count: 0,
                last_login: new Date().toISOString(),
                last_interaction: new Date().toISOString(),
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            };
            
            return this.careData;
        }
    }

    async updateCare(actionType, metrics = {}) {
        if (!this.isInitialized) {
            console.warn('CareManager not initialized');
            return null;
        }

        try {
            this.careData = await this.gameService.fetchApi('/api/care/update', {
                method: 'POST',
                body: JSON.stringify({
                    walletAddress: this.gameService.walletAddress,
                    actionType,
                    metrics
                })
            });
            
            this.events.emit('care-updated', this.careData);
            // console.log(`Care updated for action: ${actionType}`, this.careData);
            return this.careData;
        } catch (error) {
            console.warn(`Error updating care data for ${actionType}, continuing with local data:`, error.message);
            
            // Update local care data as fallback
            const now = new Date();
            switch (actionType) {
                case 'login':
                    this.careData.total_logins += 1;
                    this.careData.last_login = now.toISOString();
                    break;
                case 'interaction':
                    this.careData.interaction_count += 1;
                    this.careData.last_interaction = now.toISOString();
                    break;
                case 'neglect':
                    if (metrics.type === 'hunger') {
                        this.careData.hunger_neglect_count += 1;
                    } else if (metrics.type === 'energy') {
                        this.careData.energy_neglect_count += 1;
                    }
                    break;
                case 'feeding':
                    // Simple local calculation for feeding frequency
                    this.careData.feeding_frequency = Math.min(3.0, this.careData.feeding_frequency + 0.1);
                    break;
            }
            
            this.careData.updated_at = now.toISOString();
            this.events.emit('care-updated', this.careData);
            return this.careData;
        }
    }

    // Calculate happiness based on care quality, hunger, and energy
    calculateHappiness(hungerPercent, energyPercent) {
        if (!this.careData) {
            return 50; // Default happiness if no care data
        }

        let happiness = this.careData.care_score; // Start with care score (0-100)
        
        // Adjust based on current hunger and energy levels
        // Hunger is more important than energy for happiness
        if (hungerPercent < 20) {
            happiness -= 30; // Very hungry = very unhappy
        } else if (hungerPercent < 50) {
            happiness -= 15; // Somewhat hungry = somewhat unhappy
        } else if (hungerPercent > 80) {
            happiness += 10; // Well fed = happy bonus
        }

        if (energyPercent < 20) {
            happiness -= 20; // Very tired = unhappy
        } else if (energyPercent < 50) {
            happiness -= 10; // Somewhat tired = slightly unhappy
        } else if (energyPercent > 80) {
            happiness += 5; // Well rested = small happiness bonus
        }

        // Money affects happiness (being poor is stressful)
        if (this.careData.money !== undefined) {
            if (this.careData.money < 100) {
                happiness -= 15; // Very poor = unhappy
            } else if (this.careData.money < 500) {
                happiness -= 5; // Poor = slightly unhappy
            } else if (this.careData.money > 2000) {
                happiness += 10; // Rich = happy
            }
        }

        return Math.max(0, Math.min(100, Math.round(happiness)));
    }

    // Convenience methods for specific actions
    async recordLogin() {
        return await this.updateCare('login');
    }

    async recordInteraction() {
        return await this.updateCare('interaction');
    }

    async recordNeglect(type) {
        return await this.updateCare('neglect', { type });
    }

    async recordFeeding(feedingCount = 1) {
        return await this.updateCare('feeding', { feedingCount });
    }

    getCareData() {
        return this.careData;
    }

    getCareScore() {
        return this.careData ? this.careData.care_score : 50;
    }

    getConsecutiveDays() {
        return this.careData ? this.careData.consecutive_days : 0;
    }

    cleanup() {
        this.events.removeAllListeners();
        this.careData = null;
        this.isInitialized = false;
    }
}

// Export singleton instance
export const careManager = new CareManager();

// Export class for direct instantiation if needed
export default CareManager; 